import { List } from '@zorro/design-system/v2/List';
import { Title } from '@zorro/design-system/v2/Title';
import { Stack } from '@zorro/web/design-system';

import {
  PlanYearSetupFormInputs,
  usePlanYearSetupForm,
} from '../../PlanYearSetupForm';
import { AddEmployerStep } from '../AddEmployerStep';
import {
  AddEmployerStepType,
  HandleStepSubmit,
} from '../AddEmployerStep/AddEmployerUtils';
import FaqSection from '../FaqSection/FaqSection';

const AddEmployerPlanYearSetupStepFaq = () => {
  return (
    <FaqSection
      links={[{ name: 'Zorro Pay vs. self-pay', href: '#', isDrawer: true }]}
    >
      <Stack>
        <Title order={3}>Understanding payment methods</Title>
        Zorro Pay:
        <List type="ordered">
          <List.Item>
            Zorro sets up a dedicated FBO (For the Benefit Of) bank account for
            the company’s health benefit funds.
          </List.Item>
          <List.Item>
            Monthly, Zorro draws funds to cover all employee benefits.
          </List.Item>
          <List.Item>
            Employees receive a virtual payment card or bank account for carrier
            payments.
          </List.Item>
          <List.Item>
            Employee contributions are calculated by Zorro and captured in our
            payroll tool.
          </List.Item>
        </List>
        Self-pay (reimbursement):
        <List type="ordered">
          <List.Item>
            Employees pay carriers directly with their personal card or bank
            account.
          </List.Item>
          <List.Item>
            Reimbursements are calculated by Zorro and captured in our payroll
            tool.
          </List.Item>
          <List.Item>
            Employer contributions are reimbursed to employees through payroll.
          </List.Item>
        </List>
      </Stack>
    </FaqSection>
  );
};

type Props = {
  employerId: string;
  isFinalizationMode?: boolean;
};

export const AddEmployerPlanYearSetupStep = ({
  employerId,
  isFinalizationMode = false,
}: Props) => {
  const {
    planYearSetupForm,
    isAllowanceModelRequired,
    allowanceModelSourceNote,
    allowanceModelSourceLabel,
    existingAllowanceModel,
    planYearUpdateErrors,
    onSubmit,
  } = usePlanYearSetupForm({
    shouldFallbackToEarliestPeriod: true,
    isFinalizationMode,
    employerId,
  });

  const handleStepSubmit: HandleStepSubmit = async () => {
    const { success } = await onSubmit();
    if (success) {
      return { navigate: true, employerId };
    }

    return { navigate: false };
  };

  const isSubmitDisabled = !planYearSetupForm.formState.isValid;

  return (
    <AddEmployerStep
      isFinalizationMode={isFinalizationMode}
      step={AddEmployerStepType.PLAN_YEAR_SETUP}
      subtitle="will not be included in the employer invitation"
      title="Set up the company’s first plan year"
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleStepSubmit}
      employerId={employerId}
      secondaryLabel="Skip"
      isDon={false}
      rightSection={<AddEmployerPlanYearSetupStepFaq />}
    >
      <PlanYearSetupFormInputs
        planYearSetupForm={planYearSetupForm}
        isFinalizationMode={isFinalizationMode}
        isAllowanceModelRequired={isAllowanceModelRequired}
        planYearUpdateErrors={planYearUpdateErrors}
        employerId={employerId}
        allowanceModelSourceNote={allowanceModelSourceNote}
        allowanceModelSourceLabel={allowanceModelSourceLabel}
        existingAllowanceModel={existingAllowanceModel}
      />
    </AddEmployerStep>
  );
};
